import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import EnvironmentManager from '../config/EnvironmentManager';
import './VideoCourseVideoUpload.css';
import { 
  Home, Upload, 
  Video, AlertCircle, 
  CheckCircle, XCircle, Info,
  ArrowRight, BookOpen, Layers, PlayCircle,
  ChevronDown, ChevronUp, FileUp, Trash2,
  ArrowLeft, FileText, Film, HelpCircle,
  Check, X, UploadCloud, User, DollarSign,
  Globe, Award, Tag, Clock, Star, Users,
  BarChart3, Calendar, Clock as ClockIcon,
  Shield, TrendingUp, Target,
  RefreshCw
} from 'lucide-react';

const VideoCourseVideoUpload = () => {
    const auth = getAuth(app);
    const navigate = useNavigate();
    const location = useLocation();
    const { courseId } = useParams();
    
    const [courseData, setCourseData] = useState({
        courseInfo: {},
        modules: []
    });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [authChecked, setAuthChecked] = useState(false);
    const [user, setUser] = useState(null);
    
    const [videoFiles, setVideoFiles] = useState([]);
    const [videoMapping, setVideoMapping] = useState({});
    const [expandedModules, setExpandedModules] = useState({});
    const [expandedSubmodules, setExpandedSubmodules] = useState({});
    const [showInstructions, setShowInstructions] = useState(true);
    
    const editMode = location.state?.editMode || false;
    const fromCourseDetail = location.state?.fromCourseDetail || false;
    const fromExcelUpdate = location.state?.fromExcelUpdate || false;
    const fromExcelPage = location.state?.fromExcelPage || false;
    const excelAlreadyUploaded = location.state?.excelAlreadyUploaded || false;
    const excelUpdated = location.state?.excelUpdated || false;
    const refreshData = location.state?.refreshData || false;
    
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const [dataVersion, setDataVersion] = useState(0);

    useEffect(() => {
        
        console.log('courseId from params:', courseId);
        console.log('location.state:', location.state);
     
        
    }, [courseId, location.state, fromExcelUpdate, excelUpdated, refreshData, dataVersion]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthChecked(true);
        });
        return () => unsubscribe();
    }, [auth]);

    useEffect(() => {
        if (courseId && authChecked && user) {
            fetchCourseData();
        }
    }, [courseId, authChecked, user, dataVersion]);

    const fetchCourseData = async () => {
        try {
            setLoading(true);
            
            const idToken = await user.getIdToken(true);
            
            console.log('Fetching course data for ID:', courseId);
            
            
            const timestamp = new Date().getTime();
            const url = `${EnvironmentManager.baseUrl}/mv-profile/video-courses/course-with-excel/${courseId}?refresh=${timestamp}`;
            
            const response = await axios.get(
                url,
                {
                    headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                }
            );
            
            console.log('Course data loaded:', response.data);
            
            let courseInfo = {};
            let modules = [];
            
            if (response.data.courseId || response.data.title) {
                courseInfo = {
                    courseId: response.data.courseId || courseId,
                    title: response.data.title || 'Untitled Course',
                    description: response.data.description || '',
                    instructor: response.data.instructor || '',
                    category: response.data.category || '',
                    language: response.data.language || 'English',
                    level: response.data.level || 'Beginner',
                    price: response.data.price || '',
                    userType: response.data.user_type || response.data.userType || 'All',
                    duration: response.data.duration || '',
                    type: response.data.type || '',
                    createdAt: response.data.created_date || '',
                    lastUpdated: response.data.updated_date || '',
                    duration: response.data.duration || '',
                    
                    rating: response.data.rating || 4.5,
                    students: response.data.students || 0,
                    lectures: response.data.lectures || 0,
                    sections: response.data.sections || 0
                };
                
                if (Array.isArray(response.data.modules)) {
                    modules = response.data.modules;
                } else if (response.data.videoModules) {
                    modules = response.data.videoModules;
                }
            } else if (response.data.courseInfo && response.data.modules) {
                courseInfo = response.data.courseInfo;
                modules = response.data.modules;
            }
            
            setCourseData({ courseInfo, modules });
            
            // Create video mapping
            const mapping = {};
            modules.forEach((module, moduleIndex) => {
                const moduleSubmodules = module.submodules || module.videoSubModules || [];
                
                moduleSubmodules.forEach((submodule, subIndex) => {
                    const submoduleVideos = submodule.coursevideo || submodule.videoCourseMedia || submodule.videos || [];
                    
                    submoduleVideos.forEach((video, videoIndex) => {
                        const videoId = video.courseVideoId || video.videoId || `video-${moduleIndex}-${subIndex}-${videoIndex}`;
                        const videoTitle = video.videoCourseTitle || video.title || `Video ${videoIndex + 1}`;
                        
                        if (videoId) {
                            mapping[videoId] = {
                                file: null,
                                title: videoTitle,
                                moduleTitle: module.title || `Module ${moduleIndex + 1}`,
                                submoduleTitle: submodule.title || `Submodule ${subIndex + 1}`,
                                moduleIndex,
                                subIndex,
                                videoIndex,
                                originalTitle: videoTitle,
                                existingVideoUrl: video.videoCourseUrl || video.videoUrl 
                            };
                        }
                    });
                });
            });
            
            setVideoMapping(mapping);
            
            if (modules.length > 0) {
                setExpandedModules({ 0: true });
            }
            
           
            setLastRefreshTime(new Date().toLocaleTimeString());
            
           
            if (fromExcelUpdate || excelUpdated) {
                showMessage('success', 'Course structure updated successfully! Please check the new structure below.');
            }
            
        } catch (error) {
            console.error('Error fetching course data:', error);
            showMessage('error', `Failed to load course: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleVideoSelect = (videoId, event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        if (!file.type.startsWith('video/')) {
            showMessage('error', 'Please select a video file (mp4, mov, avi, mkv, etc.)');
            return;
        }
        
        if (file.size > 1024 * 1024 * 1024) {
            showMessage('error', 'File size too large. Maximum 1GB allowed.');
            return;
        }
        
        const fileName = file.name.replace(/\.[^/.]+$/, "").toLowerCase().trim();
        const videoTitle = videoMapping[videoId]?.originalTitle?.toLowerCase().trim();
        
        let matchStatus = 'partial';
        if (fileName === videoTitle) {
            matchStatus = 'exact';
        } else if (videoTitle && fileName.includes(videoTitle) || videoTitle.includes(fileName)) {
            matchStatus = 'partial';
        } else {
            matchStatus = 'none';
        }
        
        setVideoMapping(prev => ({
            ...prev,
            [videoId]: {
                ...prev[videoId],
                file: file,
                fileName: file.name,
                fileSize: file.size,
                matchStatus: matchStatus
            }
        }));
        
        setVideoFiles(prev => {
            const existingIndex = prev.findIndex(f => f.videoId === videoId);
            if (existingIndex !== -1) {
                const newFiles = [...prev];
                newFiles[existingIndex] = { videoId, file };
                return newFiles;
            }
            return [...prev, { videoId, file }];
        });
        
        if (matchStatus === 'exact') {
            showMessage('success', `Perfect! "${file.name}" matches the video title`);
        } else if (matchStatus === 'partial') {
            showMessage('warning', `Uploading "${file.name}". Make sure this is the correct video for: "${videoMapping[videoId]?.originalTitle}"`);
        } else {
            showMessage('warning', `Filename doesn't match. Please verify this is the correct video for: "${videoMapping[videoId]?.originalTitle}"`);
        }
    };

    const removeVideo = (videoId) => {
        setVideoMapping(prev => ({
            ...prev,
            [videoId]: {
                ...prev[videoId],
                file: null,
                fileName: null,
                fileSize: null,
                matchStatus: null
            }
        }));
        
        setVideoFiles(prev => prev.filter(f => f.videoId !== videoId));
        showMessage('info', 'Video removed');
    };

    const toggleModule = (index) => {
        setExpandedModules(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const toggleSubmodule = (moduleIndex, submoduleIndex) => {
        const key = `${moduleIndex}-${submoduleIndex}`;
        setExpandedSubmodules(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const getSelectedCount = () => {
        return Object.values(videoMapping).filter(v => v.file).length;
    };

    const getTotalCount = () => {
        return Object.keys(videoMapping).length;
    };

    const handleUploadVideos = async () => {
        if (!user) {
            showMessage('error', 'Please login first');
            return;
        }
        
        const selectedCount = getSelectedCount();
        
       
        const hasExistingVideos = Object.values(videoMapping).some(
            video => video.existingVideoUrl
        );
        
        if (selectedCount === 0) {
            
            if (editMode && hasExistingVideos) {
            const confirmUpload = window.confirm(
                'No new videos selected. The course will be updated using existing videos.\nContinue?'
            );
            if (!confirmUpload) return;
            }
            
            else {
            const confirmUpload = window.confirm(
                editMode 
                ? 'No videos selected. Course details will be updated without video changes.\nContinue?'
                : 'No videos selected. Course will be saved without videos. You can add videos later.\nContinue?'
            );
            if (!confirmUpload) return;
            }
        }
        
        setUploading(true);
        
        try {
            const idToken = await user.getIdToken(true);
            
            const formData = new FormData();
            
            Object.entries(videoMapping).forEach(([videoId, videoData]) => {
            if (videoData.file) {
                formData.append('videos', videoData.file);
            }
            });
            
            if (selectedCount === 0) {
            formData.append('videos', new Blob(), 'empty');
            }
            
            console.log(`${editMode ? 'Updating' : 'Uploading'} ${selectedCount} videos for course: ${courseId}`);
            
            const endpoint = editMode
            ? `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.updateVideoCourseVideos}/${courseId}`
            : `${EnvironmentManager.baseUrl}/mv-profile/video-courses/upload-course-videos/${courseId}`;
            
            const response = await axios.post(
            endpoint,
            formData,
            {
                headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${idToken}`
                },
                timeout: 300000
            }
            );
            
           
            const isSuccess = response.status === 200 || response.status === 201;
            
            if (selectedCount === 0) {
            showMessage('success', editMode 
                ? 'Course updated successfully! No video changes made.' 
                : 'Course saved successfully! No videos uploaded. You can add videos later.');
            } else {
            showMessage('success', editMode 
                ? `${selectedCount} video(s) updated successfully!`
                : `${selectedCount} video(s) uploaded successfully!`);
            }
            
           
            const { updateType, redirectTo } = location.state || {};
            
            setTimeout(() => {
            if (updateType === 'videosOnly' || updateType === 'both') {
               
                navigate(redirectTo || '/MHMV-profile/video-courses', {
                state: {
                    refresh: true,
                    courseId,
                    showSuccess: true,
                    message: editMode ? 'Videos updated successfully!' : 'Videos uploaded successfully!'
                }
                });
            } else {
                
                navigate('/MHMV-profile/video-courses', {
                state: {
                    refresh: true,
                    courseId,
                    showSuccess: true
                }
                });
            }
            }, 2000);
            

        } catch (error) {
            console.error('Upload error:', error);
            
            if (error.response?.data?.includes('does not match Excel title')) {
            showMessage('error', 
                'Filename mismatch! Please rename your video file to match the video title exactly.\n' +
                'Example: If video title is "Introduction to Verbs", name your file "Introduction to Verbs.mp4"'
            );
            } else {
            showMessage('error', error.response?.data || error.message || (editMode ? 'Update failed' : 'Upload failed'));
            }
        } finally {
            setUploading(false);
        }
        };

    
    const handleBackToExcelUpload = () => {
        navigate(`/MHMV-profile/upload-course-excel`, {
            state: {
                courseId: courseId,
                editMode: editMode,
                fromVideoUpload: true,
                courseData: courseData,
                excelAlreadyUploaded: true
            }
        });
    };

   
    const refreshCourseData = () => {
        setDataVersion(prev => prev + 1);
        showMessage('info', 'Refreshing course data...');
    };

    const renderInstructions = () => {
        if (!showInstructions) return null;
        
        return (
            <div className="ve-instructions-card">
                <div className="ve-instructions-header">
                    <HelpCircle size={20} />
                    <h3>{editMode ? 'How to Update Videos' : 'How to Upload Videos'}</h3>
                    <button 
                        className="ve-close-instructions"
                        onClick={() => setShowInstructions(false)}
                    >
                        <X size={16} />
                    </button>
                </div>
                
                <div className="ve-instructions-content">
                    <div className="ve-instruction-step">
                        <div className="ve-step-number">1</div>
                        <div className="ve-step-content">
                            <strong>Match Video Titles</strong>
                            <p>Rename your video files to match the video titles shown below.</p>
                            <p className="ve-example">
                                <strong>Example:</strong> If title is "Introduction to Verbs", 
                                name your file <code>Introduction to Verbs.mp4</code>
                            </p>
                        </div>
                    </div>
                    
                    <div className="ve-instruction-step">
                        <div className="ve-step-number">2</div>
                        <div className="ve-step-content">
                            <strong>Select Video Files</strong>
                            <p>Click "Choose File" next to each video title and select the matching video file.</p>
                            <p>Supported formats: MP4</p>
                        </div>
                    </div>
                    
                    <div className="ve-instruction-step">
                        <div className="ve-step-number">3</div>
                        <div className="ve-step-content">
                            <strong>{editMode ? 'Update Flexibly' : 'Upload Flexibly'}</strong>
                            <p>You can {editMode ? 'update' : 'upload'}:</p>
                            <ul>
                                <li><Check size={12} /> All videos now</li>
                                <li><Check size={12} /> Some videos now, rest later</li>
                                <li><Check size={12} /> Skip videos for now</li>
                            </ul>
                            {editMode && (
                                <p className="ve-note">
                                    <strong>Note:</strong> Videos not selected will keep their existing files.
                                </p>
                            )}
                        </div>
                    </div>
                    
                    {(fromExcelUpdate || excelUpdated) && (
                        <div className="ve-excel-update-notice">
                            <h4><Info size={16} /> Excel File Recently Updated</h4>
                            <p>Your course structure has been updated. Please verify the new structure matches your video files.</p>
                            <button 
                                className="ve-refresh-data-btn-small"
                                onClick={refreshCourseData}
                            >
                                <RefreshCw size={14} />
                                Refresh Data
                            </button>
                        </div>
                    )}
                    
                    <div className="ve-filename-tips">
                        <h4>Filename Tips:</h4>
                        <ul>
                            <li>Remove special characters: <code>Introduction-to-Verbs.mp4</code> (good)</li>
                            <li>No extra words: <code>MyVideo-Introduction-to-Verbs.mp4</code> (bad)</li>
                            <li>Match case: Titles are case-insensitive</li>
                            <li>Remove file extension when comparing</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const renderCourseDetails = () => {
        const { courseInfo } = courseData;
        
        return (
            <div className="ve-course-details-section">
                <button
                            className="ve-back-to-excel-btn"
                            onClick={handleBackToExcelUpload}
                            disabled={uploading}
                        >
                            <ArrowLeft size={18} />
                            Back to Excel Upload
                        </button>
                        
                
                {/* Edit Mode Banner */}
                {editMode && (
                    <div className="ve-edit-mode-banner">
                        <div className="ve-edit-banner-content">
                            <Info size={20} />
                            <div>
                                <strong>Edit Mode:</strong> You are updating videos for this course.
                                <br />
                                <small>Only selected videos will be updated. Others will keep their existing files.</small>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Excel Update Banner */}
                {(fromExcelUpdate || excelUpdated) && (
                    <div className="ve-excel-update-banner">
                        <div className="ve-excel-banner-content">
                            <RefreshCw size={20} />
                            <div>
                                <strong>Excel File Updated:</strong> Course structure has been refreshed.
                                <br />
                                <small>Showing updated structure from the latest Excel file.</small>
                            </div>
                            
                        </div>
                    </div>
                )}
                
                {/* Main Course Header */}
                <div className="ve-course-header">
                    <div className="ve-course-header-top">
                        <h1 className="ve-course-title">{courseInfo.title || 'Course Title'}</h1>
                        <div className="ve-course-actions">
                            
                            {lastRefreshTime && (
                                <span className="ve-last-refresh">
                                    Last updated: {lastRefreshTime}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <p className="ve-course-subtitle">{courseInfo.description || 'Course description goes here'}</p>
                    
                    <div className="ve-course-meta-row">
                        <div className="ve-instructor-info">
                            <span className="ve-created-by">Created by</span>
                            <div className="ve-instructor-name">
                                <User size={14} />
                                <span>{courseInfo.instructor || 'Instructor Name'}</span>
                            </div>
                        </div>
                        
                        <div className="ve-meta-items">
                            <div className="ve-meta-item">
                                <Globe size={14} />
                                <span>{courseInfo.language || 'English'}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Rating and Stats */}
                    <div className="ve-rating-stats-row">
                        <div className="ve-rating-section">
                            <div className="ve-rating-badge">
                                <div className="ve-rating-number">{courseInfo.rating || 4.5}</div>
                                <div className="ve-rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star 
                                            key={star} 
                                            size={16} 
                                            fill={star <= Math.floor(courseInfo.rating || 4.5) ? "#f59e0b" : "none"}
                                            stroke="#f59e0b"
                                        />
                                    ))}
                                </div>
                                <div className="ve-rating-count">({(courseInfo.students || 0).toLocaleString()} ratings)</div>
                            </div>
                        </div>
                        
                        <div className="ve-stats-section">
                            <div className="ve-stat-item">
                                <Users size={16} />
                                <div className="ve-stat-content">
                                    <div className="ve-stat-number">{(courseInfo.students || 0).toLocaleString()}</div>
                                    <div className="ve-stat-label">students</div>
                                </div>
                            </div>
                            <div className="ve-stat-item">
                                <PlayCircle size={16} />
                                <div className="ve-stat-content">
                                    <div className="ve-stat-number">{courseInfo.lectures || 0}</div>
                                    <div className="ve-stat-label">lectures</div>
                                </div>
                            </div>
                            <div className="ve-stat-item">
                                <ClockIcon size={16} />
                                <div className="ve-stat-content">
                                    <div className="ve-stat-number">{courseInfo.duration || '0h'}</div>
                                    <div className="ve-stat-label">total length</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Course Content Summary */}
                <div className="ve-course-content-summary">
                    <div className="ve-summary-header">
                        <h2>Course content</h2>
                        <div className="ve-summary-stats">
                            <span>{courseInfo.sections || courseData.modules.length || 0} sections</span>
                            <span>•</span>
                            <span>{courseInfo.lectures || getTotalCount() || 0} lectures</span>
                            <span>•</span>
                            <span>{courseInfo.duration || '0h'} total length</span>
                        </div>
                    </div>
                </div>
                
                {/* Course Details Cards */}
                <div className="ve-course-details-cards">
                    <div className="ve-detail-card">
                        <div className="ve-card-icon">
                            <Target size={20} />
                        </div>
                        <div className="ve-card-content">
                            <h3>What you'll learn</h3>
                            <ul className="ve-learn-list">
                                <li>You will learn how to leverage the power of {courseInfo.category || 'the subject'} to solve tasks</li>
                                <li>
                                    {courseInfo.description}
                                </li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="ve-detail-card">
                        <div className="ve-card-icon">
                            <Award size={20} />
                        </div>
                        <div className="ve-card-content">
                            <h3>Course Details</h3>
                            <div className="ve-details-grid">
                                <div className="ve-detail-item">
                                    <div className="ve-detail-label">Level</div>
                                    <div className="ve-detail-value ve-badge ve-level">{courseInfo.level || 'Beginner'}</div>
                                </div>
                                <div className="ve-detail-item">
                                    <div className="ve-detail-label">Category</div>
                                    <div className="ve-detail-value">{courseInfo.category || 'Development'}</div>
                                </div>
                                <div className="ve-detail-item">
                                    <div className="ve-detail-label">Price</div>
                                    <div className="ve-detail-value ve-price">
                                        <span className="ve-current-price">{courseInfo.price || '999'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderVideoFileUpload = (videoId, videoData, videoIndex) => {
        const selectedFile = videoData.file;
        const matchStatus = videoData.matchStatus;
        const hasExistingVideo = !!videoData.existingVideoUrl;
        
        return (
            <div className="ve-video-upload-row">
                <div className="ve-video-title-info">
                    <span className="ve-video-number">{videoIndex + 1}.</span>
                    <div className="ve-video-title-content">
                        <span className="ve-video-title-text">{videoData.originalTitle}</span>
                        <div className="ve-title-example">
                            <small>Filename should be: </small>
                            <code>{videoData.originalTitle}.mp4</code>
                            {hasExistingVideo && !selectedFile && editMode && (
                                <span className="ve-existing-video-badge">
                                    <Check size={12} /> Has existing video
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="ve-video-file-section">
                    {selectedFile ? (
                        <div className={`ve-selected-file-container ${matchStatus}`}>
                            <div className="ve-file-info">
                                <Video size={16} />
                                <div className="ve-file-details">
                                    <span className="ve-file-name">{selectedFile.name}</span>
                                    <div className="ve-file-meta">
                                        <span className="ve-file-size">
                                            {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                                        </span>
                                        {matchStatus === 'exact' && (
                                            <span className="ve-match-badge ve-exact">
                                                <Check size={12} /> Name matches
                                            </span>
                                        )}
                                        {matchStatus === 'partial' && (
                                            <span className="ve-match-badge ve-partial">
                                                <AlertCircle size={12} /> Partial match
                                            </span>
                                        )}
                                        {matchStatus === 'none' && (
                                            <span className="ve-match-badge ve-none">
                                                <X size={12} /> Name doesn't match
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => removeVideo(videoId)}
                                className="ve-remove-file-btn"
                                title="Remove this video"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ) : (
                        <div className="ve-file-upload-area">
                            <input
                                type="file"
                                accept="video/*"
                                id={`video-file-${videoId}`}
                                onChange={(e) => handleVideoSelect(videoId, e)}
                                className="ve-file-input"
                            />
                            <label htmlFor={`video-file-${videoId}`} className="ve-upload-file-btn">
                                <FileUp size={16} />
                                {hasExistingVideo && editMode ? 'Replace Video File' : 'Choose Video File'}
                            </label>
                            <div className="ve-upload-hint">
                                <small>Select: {videoData.originalTitle}.mp4</small>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderModules = () => {
        const { modules } = courseData;
        
        if (modules.length === 0) {
            return (
                <div className="ve-empty-modules">
                    <Layers size={48} />
                    <h3>No Course Structure Found</h3>
                    <p>This course doesn't have any modules or videos defined.</p>
                    <button 
                        className="ve-back-to-excel-btn"
                        onClick={handleBackToExcelUpload}
                    >
                        <ArrowLeft size={16} />
                        Back to Excel Upload
                    </button>
                </div>
            );
        }
        
        return (
            <div className="ve-course-structure-section">
                <div className="ve-section-header">
                    <Layers size={20} />
                    <h2>{editMode ? 'Update Videos for Each Title' : 'Upload Videos for Each Title'}</h2>
                    <div className="ve-section-stats">
                        <span className="ve-stat">
                            <PlayCircle size={14} /> {getTotalCount()} videos total
                        </span>
                        <span className="ve-stat">
                            <Check size={14} /> {getSelectedCount()} selected
                        </span>
                        <button 
                            className="ve-refresh-small-btn"
                            onClick={refreshCourseData}
                            title="Refresh structure"
                        >
                            <RefreshCw size={14} />
                        </button>
                    </div>
                </div>
                
                {fromExcelUpdate && (
                    <div className="ve-excel-update-alert">
                        <Info size={16} />
                        <span><strong>Note:</strong> Excel file was recently updated. Please verify the new structure matches your video files.</span>
                    </div>
                )}
                
                <div className="ve-modules-accordion">
                    {modules.map((module, moduleIndex) => {
                        const submodules = module.submodules || module.videoSubModules || [];
                        
                        const totalVideos = submodules.reduce((total, submodule) => {
                            const videos = submodule.coursevideo || submodule.videoCourseMedia || submodule.videos || [];
                            return total + videos.length;
                        }, 0);
                        
                        const selectedInModule = submodules.reduce((total, submodule) => {
                            const videos = submodule.coursevideo || submodule.videoCourseMedia || submodule.videos || [];
                            return total + videos.filter(video => {
                                const videoId = video.courseVideoId || video.videoId;
                                return videoId && videoMapping[videoId]?.file;
                            }).length;
                        }, 0);
                        
                        return (
                            <div key={module.moduleId || moduleIndex} className="ve-module-accordion-item">
                                <div 
                                    className="ve-module-accordion-header"
                                    onClick={() => toggleModule(moduleIndex)}
                                >
                                    <div className="ve-module-header-content">
                                        <div className="ve-module-icon-title">
                                            <div className="ve-module-number-circle">
                                                {moduleIndex + 1}
                                            </div>
                                            <div>
                                                <h3 className="ve-module-title">
                                                    {module.title || `Module ${moduleIndex + 1}`}
                                                </h3>
                                                {module.description && (
                                                    <p className="ve-module-description">
                                                        {module.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="ve-module-header-stats">
                                            <div className="ve-video-progress">
                                                <div className="ve-progress-text">
                                                    {selectedInModule}/{totalVideos} videos
                                                </div>
                                                {totalVideos > 0 && (
                                                    <div className="ve-progress-bar">
                                                        <div 
                                                            className="ve-progress-fill"
                                                            style={{ width: `${(selectedInModule / totalVideos) * 100}%` }}
                                                        ></div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="ve-module-toggle">
                                                {expandedModules[moduleIndex] ? 
                                                    <ChevronUp size={20} /> : 
                                                    <ChevronDown size={20} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {expandedModules[moduleIndex] && (
                                    <div className="ve-module-accordion-content">
                                        {submodules.map((submodule, subIndex) => {
                                            const videos = submodule.coursevideo || submodule.videoCourseMedia || submodule.videos || [];
                                            const selectedInSubmodule = videos.filter(video => {
                                                const videoId = video.courseVideoId || video.videoId;
                                                return videoId && videoMapping[videoId]?.file;
                                            }).length;
                                            
                                            return (
                                                <div key={submodule.subModuleId || subIndex} className="ve-submodule-section">
                                                    <div 
                                                        className="ve-submodule-header"
                                                        onClick={() => toggleSubmodule(moduleIndex, subIndex)}
                                                    >
                                                        <div className="ve-submodule-header-content">
                                                            <div className="ve-submodule-icon-title">
                                                                <div className="ve-submodule-number">
                                                                    {moduleIndex + 1}.{subIndex + 1}
                                                                </div>
                                                                <div>
                                                                    <h4 className="ve-submodule-title">
                                                                        {submodule.title || `Submodule ${subIndex + 1}`}
                                                                    </h4>
                                                                    {submodule.description && (
                                                                        <p className="ve-submodule-description">
                                                                            {submodule.description}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="ve-submodule-header-stats">
                                                                <span className="ve-video-count">
                                                                    {selectedInSubmodule}/{videos.length} selected
                                                                </span>
                                                                <div className="ve-submodule-toggle">
                                                                    {expandedSubmodules[`${moduleIndex}-${subIndex}`] ? 
                                                                        <ChevronUp size={18} /> : 
                                                                        <ChevronDown size={18} />
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {expandedSubmodules[`${moduleIndex}-${subIndex}`] && videos.length > 0 && (
                                                        <div className="ve-submodule-content">
                                                            <div className="ve-videos-list-container">
                                                                <div className="ve-videos-list-header">
                                                                    <h5>Videos in this Submodule</h5>
                                                                </div>
                                                                
                                                                <div className="ve-videos-list">
                                                                    {videos.map((video, videoIndex) => {
                                                                        const videoId = video.courseVideoId || video.videoId || `vid-${moduleIndex}-${subIndex}-${videoIndex}`;
                                                                        const videoData = videoMapping[videoId];
                                                                        
                                                                        if (!videoData) return null;
                                                                        
                                                                        return (
                                                                            <div key={videoId} className="ve-video-item-card">
                                                                                {renderVideoFileUpload(videoId, videoData, videoIndex)}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderUploadStatus = () => {
        const selectedCount = getSelectedCount();
        const totalCount = getTotalCount();
        const percentage = totalCount > 0 ? Math.round((selectedCount / totalCount) * 100) : 0;
        
        return (
            <div className="ve-upload-status-card">
                <div className="ve-status-card-header">
                    <UploadCloud size={20} />
                    <h3>{editMode ? 'Ready to Update' : 'Ready to Upload'}</h3>
                    <button 
                        className="ve-refresh-status-btn"
                        onClick={refreshCourseData}
                        title="Refresh data"
                    >
                        <RefreshCw size={14} />
                    </button>
                </div>
                
                <div className="ve-status-card-content">
                    <div className="ve-upload-progress">
                        <div className="ve-progress-text-large">
                            {selectedCount} of {totalCount} videos selected ({percentage}%)
                        </div>
                        {totalCount > 0 && (
                            <div className="ve-progress-bar-large">
                                <div 
                                    className="ve-progress-fill-large"
                                    style={{ width: `${percentage}%` }}
                                ></div>
                            </div>
                        )}
                    </div>
                    
                    <div className="ve-upload-notes">
                        {editMode && selectedCount === 0 && (
                            <div className="ve-note-item ve-note-warning">
                                <AlertCircle size={16} />
                                <span>
                                    <strong>No videos selected:</strong> Course will be updated using existing videos.
                                </span>
                            </div>
                        )}
                        
                        {(fromExcelUpdate || excelUpdated) && (
                            <div className="ve-note-item ve-note-info">
                                <Info size={16} />
                                <span>
                                    <strong>Excel Updated:</strong> Make sure your video files match the new structure.
                                </span>
                            </div>
                        )}
                        
                        <div className="ve-note-item">
                            <Info size={16} />
                            <span>
                                You can {editMode ? 'update' : 'upload'} <strong>{selectedCount} video(s)</strong> now {editMode ? 'and keep the rest as is' : 'and add the remaining later'}.
                            </span>
                        </div>
                        <div className="ve-note-item">
                            <Info size={16} />
                            <span>
                                Make sure your video filenames match the titles shown above for proper mapping.
                            </span>
                        </div>
                        {editMode && (
                            <div className="ve-note-item">
                                <Info size={16} />
                                <span>
                                    <strong>Note:</strong> Only selected videos will be updated. Others will keep their existing files.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (!authChecked) {
        return (
            <div className="ve-loading-fullscreen">
                <div className="ve-spinner-large"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="ve-login-required-page">
                <div className="ve-login-required-content">
                    <h2>Login Required</h2>
                    <p>Please login to upload video files</p>
                    <button 
                        onClick={() => navigate('/MHMV-profile/jobseeker-login')}
                        className="ve-login-button"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="ve-loading-fullscreen">
                <div className="ve-spinner-large"></div>
                <p>Loading course structure...</p>
            </div>
        );
    }

    if (!courseData.modules || courseData.modules.length === 0) {
        return (
            <div className="ve-error-page">
                <div className="ve-error-content">
                    <AlertCircle size={48} />
                    <h2>Course Not Found</h2>
                    <p>Unable to load course with ID: {courseId}</p>
                    <p className="ve-error-detail">The course structure might not exist or you don't have permission to access it.</p>
                    <div className="ve-error-actions">
                        
                        
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ve-container">
            <header className="ve-main-header">
                <div className="ve-header-content">
                    <div className="ve-header-left">
                        <div className="ve-header-titles">
                            <h1>{editMode ? 'Update Course Videos' : 'Upload Course Videos'}</h1>
                            <p className="ve-header-subtitle">
                                {editMode ? 'Update video files for your course' : 'Step 2: Upload video files for your course'}
                                {fromExcelUpdate && ' (Excel recently updated)'}
                            </p>
                        </div>
                    </div>
                    
                    <div className="ve-header-right">
                        <div className="ve-user-profile">
                            <div className="ve-user-avatar-small">
                                {user.email?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <span className="ve-user-email">{user.email}</span>
                        </div>
                    </div>
                </div>
            </header>
            
            <main className="ve-main-content-area">
                {message.text && (
                    <div className={`ve-floating-message ${message.type}`}>
                        <div className="ve-message-content">
                            {message.type === 'success' && <CheckCircle size={18} />}
                            {message.type === 'error' && <XCircle size={18} />}
                            {message.type === 'warning' && <AlertCircle size={18} />}
                            {message.type === 'info' && <Info size={18} />}
                            <span>{message.text}</span>
                        </div>
                    </div>
                )}
                
                {renderCourseDetails()}
                
                <div className="ve-content-grid">
                    <div className="ve-left-column">
                        {renderInstructions()}
                        {renderUploadStatus()}
                    </div>
                    
                    <div className="ve-right-column">
                        {renderModules()}
                    </div>
                </div>
                
                <div className="ve-action-footer">
                    <div className="ve-footer-left">
                        
                        
                        <button
                            className="ve-secondary-action-btn"
                            onClick={() => {
                                if (fromCourseDetail) {
                                    navigate('/MHMV-profile/video-courses');
                                } else if (editMode) {
                                    navigate('/MHMV-profile/video-courses');
                                } else {
                                    navigate('/MHMV-profile/video-course-home');
                                }
                            }}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                        
                        
                        
                    </div>
                    
                    <div className="ve-footer-right">
                        <div className="ve-upload-summary">
                            <span className="ve-summary-text">
                                {editMode ? 'Updating' : 'Uploading'} <strong>{getSelectedCount()}</strong> of <strong>{getTotalCount()}</strong> videos
                            </span>
                            {(fromExcelUpdate || excelUpdated) && (
                                <span className="ve-excel-update-indicator">
                                    <Info size={14} /> Excel Updated
                                </span>
                            )}
                        </div>
                        
                        <button
                            className="ve-primary-action-btn"
                            onClick={handleUploadVideos}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <>
                                    <div className="ve-button-spinner"></div>
                                    {editMode ? 'Updating...' : 'Uploading...'}
                                </>
                            ) : (
                                <>
                                    <Upload size={18} />
                                    {editMode ? 'Update Videos' : 'Upload Videos'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default VideoCourseVideoUpload;